import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { MessageSquare } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useExtendTrial } from '../../hooks/useReview';

interface Connection {
  provider: 'QBO' | 'XERO';
  status: 'CONNECTED' | 'DISCONNECTED';
}

interface OrgSidebarProps {
  orgId: string;
  orgName: string;
  slug: string;
  createdAt: string | null;
  ownerEmail: string | null;
  tier?: string;
  connections: Connection[];
  // Billing — added for Extend Trial UI.
  trialEndsAt: string | null;
  subscriptionStatus: string | null;
  trialExtendedAt: string | null;
  trialExtendedReason: string | null;
}

export function OrgSidebar({
  orgId,
  orgName,
  slug,
  createdAt,
  ownerEmail,
  tier = '$99/mo',
  connections,
  trialEndsAt,
  subscriptionStatus,
  trialExtendedAt,
  trialExtendedReason,
}: OrgSidebarProps) {
  return (
    <aside className="w-[280px] shrink-0 border-r border-surface-200 bg-surface-0 overflow-y-auto">
      <section className="px-5 py-4 border-b border-surface-200">
        <div className="text-[9px] font-semibold tracking-[1.2px] uppercase text-surface-400 mb-1">
          Client
        </div>
        <h2 className="text-base font-bold text-surface-900">{orgName}</h2>
        <div className="text-[11px] text-surface-500 mt-0.5">{slug}</div>
        {createdAt && (
          <div className="text-[10px] text-surface-400 mt-2">
            Joined{' '}
            {new Date(createdAt).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            })}
          </div>
        )}
        {ownerEmail && <div className="text-[10px] text-surface-500 mt-0.5">{ownerEmail}</div>}
      </section>

      <section className="px-5 py-4 border-b border-surface-200">
        <div className="text-[9px] font-semibold tracking-[1.2px] uppercase text-surface-400 mb-1">
          Tier
        </div>
        <Badge variant="secondary">{tier}</Badge>
      </section>

      <ExtendTrialBlock
        orgId={orgId}
        trialEndsAt={trialEndsAt}
        subscriptionStatus={subscriptionStatus}
        trialExtendedAt={trialExtendedAt}
        trialExtendedReason={trialExtendedReason}
      />

      <section className="px-5 py-4 border-b border-surface-200">
        <div className="text-[9px] font-semibold tracking-[1.2px] uppercase text-surface-400 mb-2">
          Connections
        </div>
        {connections.length === 0 ? (
          <div className="text-[10px] text-surface-500">No accounting connections</div>
        ) : (
          <ul className="space-y-1.5">
            {connections.map((conn) => (
              <li key={conn.provider} className="flex items-center justify-between text-[11px]">
                <span className="text-surface-700">{conn.provider}</span>
                <Badge variant={conn.status === 'CONNECTED' ? 'default' : 'secondary'}>
                  {conn.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="px-5 py-4 border-b border-surface-200">
        <Link to={`/orgs/${orgId}/chat`}>
          <Button variant="outline" size="sm" className="w-full">
            <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.4} />
            Open chat
          </Button>
        </Link>
      </section>
    </aside>
  );
}

interface ExtendTrialBlockProps {
  orgId: string;
  trialEndsAt: string | null;
  subscriptionStatus: string | null;
  trialExtendedAt: string | null;
  trialExtendedReason: string | null;
}

function ExtendTrialBlock({
  orgId,
  trialEndsAt,
  subscriptionStatus,
  trialExtendedAt,
  trialExtendedReason,
}: ExtendTrialBlockProps) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState(30);
  const [reason, setReason] = useState('');
  const mut = useExtendTrial(orgId);

  const isPaid = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  const trialEnd = trialEndsAt ? new Date(trialEndsAt) : null;
  // useState initializer runs once at mount — acceptable to React Compiler as pure.
  const [nowMs] = useState<number>(() => Date.now());
  const expired = !!trialEnd && trialEnd.getTime() < nowMs;
  const daysLeft = trialEnd
    ? Math.ceil((trialEnd.getTime() - nowMs) / (24 * 60 * 60 * 1000))
    : null;

  const submit = () => {
    if (reason.trim().length < 3) return;
    mut.mutate(
      { days, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`Extended trial by ${days} days`);
          setOpen(false);
          setReason('');
        },
        onError: (err: Error) => toast.error(err.message),
      },
    );
  };

  return (
    <section className="px-5 py-4 border-b border-surface-200">
      <div className="text-[9px] font-semibold tracking-[1.2px] uppercase text-surface-400 mb-2">
        Trial
      </div>

      {isPaid ? (
        <div className="text-[11px] text-surface-700">
          Subscription is{' '}
          <span className="font-semibold">{subscriptionStatus}</span> — no trial.
        </div>
      ) : trialEnd ? (
        <div className="text-[11px] text-surface-700">
          {expired
            ? `Trial ended ${trialEnd.toLocaleDateString()}`
            : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left (${trialEnd.toLocaleDateString()})`}
        </div>
      ) : (
        <div className="text-[11px] text-surface-500">No trial set</div>
      )}

      {trialExtendedAt && (
        <div className="text-[10px] text-surface-500 mt-1">
          Last extended {new Date(trialExtendedAt).toLocaleDateString()}
          {trialExtendedReason ? ` — ${trialExtendedReason}` : ''}
        </div>
      )}

      {!isPaid && !open && (
        <Button variant="outline" size="sm" className="mt-2" onClick={() => setOpen(true)}>
          Extend trial
        </Button>
      )}

      {!isPaid && open && (
        <div className="mt-2 space-y-2">
          <label className="block text-[10px] font-medium text-surface-700">Days</label>
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-full border border-surface-300 rounded px-2 py-1 text-xs"
          />
          <label className="block text-[10px] font-medium text-surface-700">Reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. pilot SME"
            className="w-full border border-surface-300 rounded px-2 py-1 text-xs"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={submit}
              disabled={mut.isPending || reason.trim().length < 3}
            >
              Confirm
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
